import { useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, MapPin, Phone, Send, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { siteConfig } from '@/config/site'
import { submitInquiry } from '@/config/firebase'
import { sendContactEmail } from '@/config/email'
import { SEO } from '@/components/SEO'

export function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    const formData = new FormData(e.currentTarget)

    // Get selected services
    const services: string[] = []
    siteConfig.services.forEach((service) => {
      if (formData.get(`service-${service.id}`)) {
        services.push(service.id)
      }
    })

    const inquiryData = {
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string || undefined,
      businessName: formData.get('businessName') as string || undefined,
      services,
      message: formData.get('message') as string,
    }

    try {
      // Save to Firebase and send email via Cloudflare Worker
      await Promise.all([
        submitInquiry(inquiryData),
        sendContactEmail(inquiryData),
      ])

      setIsSubmitted(true)
    } catch (err) {
      console.error('Error submitting form:', err)
      setError('Something went wrong. Please try again or email us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <SEO
        title="Contact Us - Get a Free Quote"
        description="Get a free quote for your website, mobile app, or digital marketing project. Contact NEXAIGEN today - we respond within 24 hours. Call, email, or fill out our form."
        keywords={[
          'contact web designer',
          'get website quote',
          'free website consultation',
          'web design enquiry',
          'contact digital agency UK',
        ]}
        url="https://nexaigen.co.uk/contact"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pt-32 pb-20 container bg-background min-h-screen flex flex-col"
      >
        <div className="text-center mb-16">
        <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
          Let's <span className="text-primary">Talk</span>
        </h1>
        <p className="text-xl text-slate-400">
          Tell us about your project and we'll get back to you within 24 hours.
        </p>
      </div>

      <div className="flex-grow grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto w-full">
        {/* Contact Info */}
        <div className="lg:col-span-1 space-y-8">
          <div className="p-8 rounded-3xl bg-slate-900/50 border border-white/5 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-white mb-8">Get in Touch</h3>
            <div className="space-y-6">
              <a
                href={`mailto:${siteConfig.email}`}
                className="flex items-center gap-4 text-slate-400 hover:text-primary transition-colors group"
              >
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Mail size={20} />
                </div>
                <span className="text-lg">{siteConfig.email}</span>
              </a>
              <div className="flex items-center gap-4 text-slate-400">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                  <Phone size={20} />
                </div>
                <span className="text-lg">{siteConfig.phone}</span>
              </div>
              <div className="flex items-center gap-4 text-slate-400">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                  <MapPin size={20} />
                </div>
                <span className="text-lg">
                  {siteConfig.address.city}, {siteConfig.address.country}
                </span>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="p-8 rounded-3xl bg-slate-900/50 border border-white/5 backdrop-blur-sm">
            <h3 className="text-xl font-bold text-white mb-4">Business Hours</h3>
            <div className="space-y-2 text-slate-400">
              <div className="flex justify-between">
                <span>Monday - Friday</span>
                <span className="text-white">9:00 - 18:00</span>
              </div>
              <div className="flex justify-between">
                <span>Saturday</span>
                <span className="text-white">10:00 - 14:00</span>
              </div>
              <div className="flex justify-between">
                <span>Sunday</span>
                <span className="text-slate-500">Closed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="lg:col-span-2 p-8 md:p-12 rounded-3xl bg-slate-900/50 border border-white/5 backdrop-blur-sm">
          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="h-full flex flex-col items-center justify-center text-center py-12"
            >
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mb-6">
                <CheckCircle size={32} className="text-accent" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">
                Message Sent Successfully!
              </h3>
              <p className="text-slate-400 max-w-md">
                Thank you for reaching out. We'll review your inquiry and get back to you within 24 hours.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Error Message */}
              {error && (
                <div className="mb-8 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-3 text-red-400">
                  <AlertCircle size={20} />
                  <span>{error}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-2">
                  <label className="label">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="input"
                    placeholder="John Smith"
                  />
                </div>
                <div className="space-y-2">
                  <label className="label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="input"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-2">
                  <label className="label">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    className="input"
                    placeholder="+44 20 1234 5678"
                  />
                </div>
                <div className="space-y-2">
                  <label className="label">Business Name</label>
                  <input
                    type="text"
                    name="businessName"
                    className="input"
                    placeholder="Your Business Ltd"
                  />
                </div>
              </div>

              <div className="mb-8 space-y-2">
                <label className="label">Services Interested In</label>
                <div className="grid grid-cols-2 gap-4">
                  {siteConfig.services.map((service) => (
                    <label
                      key={service.id}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <input
                        type="checkbox"
                        name={`service-${service.id}`}
                        className="w-5 h-5 rounded border-white/10 bg-slate-950/50 text-primary focus:ring-primary focus:ring-offset-0"
                      />
                      <span className="text-slate-400 group-hover:text-white transition-colors">
                        {service.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-8 space-y-2">
                <label className="label">Tell us about your project *</label>
                <textarea
                  name="message"
                  required
                  rows={6}
                  className="input resize-none"
                  placeholder="Describe your business, goals, and what you're looking to achieve..."
                />
              </div>

              <div className="mb-8">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    required
                    className="w-5 h-5 mt-0.5 rounded border-white/10 bg-slate-950/50 text-primary focus:ring-primary focus:ring-offset-0"
                  />
                  <span className="text-sm text-slate-400">
                    I agree to the{' '}
                    <a href="/privacy" className="text-primary hover:underline">
                      Privacy Policy
                    </a>{' '}
                    and consent to being contacted about my inquiry.
                  </span>
                </label>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Send Message <Send size={18} />
                  </span>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
      </motion.div>
    </>
  )
}

export default Contact
